import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import Button from 'components/Button'
import ConfirmModal from 'components/ConfirmModal'
import Icon from 'components/Icon'
import ProgressBar from 'components/ProgressBar'
import { addModal } from 'features/modals/modalSlice'
import { colors, _ } from 'utils'
import { progressSelector, setIsMinimizing } from './importExportSlice'

const S = {
    Header: styled.div`
        display: flex;
        align-items: center;
        justify-content: space-between;
    `,
    Title: styled.h3`
        margin-bottom: 0;
        font-weight: 500;
        font-size: 30px;
        line-height: 35px;
        color: ${colors.dark};
    `,
    Actions: styled.div`
        display: flex;
        margin-right: -6px;
    `,
    Description: styled.p`
        margin: 16px 0;
        font-weight: 300;
        font-size: 16px;
        line-height: 19px;
        color: ${colors.grayDescription};
    `,
}

const ImportProgress = ({ close }: { close: () => void }) => {
    const dispatch = useDispatch()
    const progress = useSelector(progressSelector)
    const isCompleted = progress.completed >= progress.total
    const minimizedModal = localStorage.getItem('minimized_modal') === 'true'

    const handleMinimizeModal = () => {
        close()
        if (minimizedModal) {
            return dispatch(setIsMinimizing(true))
        }
        localStorage.setItem('minimized_modal', 'true')
        dispatch(
            addModal({
                header: _('import_file.minimize_modal.continue_processing_title'),
                Component: ({ close: _close }: { close: () => void }) => (
                    <>
                        <S.Description>
                            {_('import_file.minimize_modal.continue_processing_description')}
                        </S.Description>
                        <Button
                            variant="secondary"
                            style={{ display: 'inherit', marginLeft: 'auto' }}
                            onClick={_close}
                        >
                            {_('button.got_it')}
                        </Button>
                    </>
                ),
                callback: () => dispatch(setIsMinimizing(true))
            })
        )
    }

    const cancelUploading = () => {
        if (isCompleted) {
            return close()
        }
        localStorage.setItem('import-errors', progress.errorItems.join(', '))
        window.location.reload()
    }

    const handleClose = () => {
        if (isCompleted) {
            return close()
        }

        dispatch(
            addModal({
                Component: ({ close: confirmClose }: { close: () => void }) => (
                    <ConfirmModal
                        close={confirmClose}
                        title={_('import_file.cancel_upload_confirm.title')}
                        confirmDescription={_('import_file.cancel_upload_confirm.description')}
                        variantConfirm="danger"
                        onConfirm={cancelUploading}
                    />
                ),
                shouldNotWrap: true,
            })
        )
    }

    return (
        <div>
            <S.Header>
                <S.Title>
                    {isCompleted ? _('import_file.completed_title') : _('import_file.uploading_title')}
                </S.Title>
                <S.Actions>
                    {!isCompleted && (
                        <Icon icon="minus" onClick={handleMinimizeModal} size={26} className="mr-1" />
                    )}
                    <Icon icon="close" onClick={handleClose} size={26} />
                </S.Actions>
            </S.Header>
            <S.Description>
                {isCompleted
                    ? _('import_file.completed_description')
                    : _('import_file.uploading_description')}
            </S.Description>
            <ProgressBar progress={progress} errors={progress.errorItems} />
            <Button variant="secondary" onClick={handleClose} style={{ marginTop: 40 }}>
                {isCompleted ? _('button.close') : _('button.cancel')}
            </Button>
        </div>
    )
}

export default ImportProgress
